import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttendanceRecordResponse } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { attendanceApi } from '@/services/attendance.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';
import { CheckInDialog } from './check-in-dialog';

/**
 * Attendance: a log of check-ins, most recent first, with an inline
 * check-out — no detail page, since a record has nothing else to show once
 * who, where and when are on the row.
 */
export function AttendanceListPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [openOnly, setOpenOnly] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);

  const query = useQuery({
    queryKey: ['attendance', { openOnly }],
    queryFn: ({ signal }) => attendanceApi.list({ openOnly, limit: 50 }, signal),
    placeholderData: (previous) => previous,
  });

  const checkOut = useMutation({
    mutationFn: (id: string) => attendanceApi.checkOut(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['attendance'], exact: false });
    },
  });

  const records = query.data?.data ?? [];
  const isStale = query.isPlaceholderData;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('attendance.record.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('attendance.record.count', { count: records.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>

        {can('attendance.record.create') && (
          <div className="page-actions">
            <Link className="btn" to="/attendance/scan">
              {t('attendance.scan.action')}
            </Link>
            <button
              type="button"
              className="btn"
              data-variant="primary"
              onClick={() => setIsCheckingIn(true)}
            >
              {t('attendance.record.checkIn')}
            </button>
          </div>
        )}
      </header>

      <div className="filters">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(event) => setOpenOnly(event.target.checked)}
          />
          <span>{t('attendance.record.openOnly')}</span>
        </label>
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}
      {checkOut.isError && (
        <Alert tone="danger">
          {checkOut.error instanceof ApiError
            ? t(`errors.${checkOut.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && records.length === 0 && (
        <EmptyState title={t('attendance.record.empty')} hint={t('attendance.record.checkIn')} />
      )}

      {records.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('people.person.singular')}</th>
                <th scope="col">{t('attendance.record.contextType')}</th>
                <th scope="col">{t('attendance.record.checkedInAt')}</th>
                <th scope="col">{t('attendance.record.checkedOutAt')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <AttendanceRow
                  key={record.id}
                  record={record}
                  locale={locale}
                  canCheckOut={can('attendance.record.update')}
                  checkingOut={checkOut.isPending && checkOut.variables === record.id}
                  onCheckOut={() => checkOut.mutate(record.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCheckingIn && (
        <CheckInDialog
          onClose={() => setIsCheckingIn(false)}
          onCheckedIn={() => {
            setIsCheckingIn(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function AttendanceRow({
  record,
  locale,
  canCheckOut,
  checkingOut,
  onCheckOut,
}: {
  record: AttendanceRecordResponse;
  locale: string;
  canCheckOut: boolean;
  checkingOut: boolean;
  onCheckOut: () => void;
}) {
  const { t } = useI18n();

  return (
    <tr>
      <td>
        <Link to={`/people/${record.personId}`} className="person-name">
          {record.displayName}
        </Link>
      </td>
      <td>
        {t(`attendance.contextType.${record.contextType}`)}
        {record.contextLabel && <span className="cell-empty"> · {record.contextLabel}</span>}
      </td>
      <td dir="ltr">{formatDateTime(record.checkedInAt, locale)}</td>
      <td dir="ltr">
        {record.checkedOutAt ? (
          formatDateTime(record.checkedOutAt, locale)
        ) : (
          <span className="cell-empty">—</span>
        )}
      </td>
      <td>
        {canCheckOut && !record.checkedOutAt && (
          <Button variant="ghost" loading={checkingOut} onClick={onCheckOut}>
            {t('attendance.record.checkOut')}
          </Button>
        )}
      </td>
    </tr>
  );
}
